<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <div class="dl-page__heading">
    <a class="dl-page__back" routerLink="/catalogs">
      <mat-icon>chevron_left</mat-icon>
      Catalogs
    </a>
  </div>

  <div class="dl-loading" *ngIf="loading">
    <div class="dl-spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="dl-banner dl-banner--error" *ngIf="error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
  </div>

  <ng-container *ngIf="catalog as c">

    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <div class="dl-page__header">
      <div class="dl-page__heading">
        <h1 class="dl-page__title">{{ c.name }}</h1>
        <p class="dl-page__subtitle catalog-meta">
          <code>{{ c.code }}</code>
          <span [ngClass]="statusClass(c.status)" data-testid="catalog-status" [attr.data-status]="c.status">
            {{ statusLabel(c.status) }}
          </span>
          <span>version {{ c.version }}</span>
        </p>
      </div>
      <div class="dl-page__actions">
        <button type="button" class="dl-btn" (click)="formOpen = true" data-testid="catalog-edit">
          <mat-icon>edit</mat-icon>
          Edit
        </button>
        <button type="button" class="dl-btn dl-btn--danger-ghost" *ngIf="canArchive"
                (click)="askAction('archive')" data-testid="catalog-archive">
          <mat-icon>archive</mat-icon>
          Archive
        </button>
        <button type="button" class="dl-btn dl-btn--primary" *ngIf="c.status !== 'PUBLISHED'"
                [disabled]="!canPublish" (click)="askAction('publish')"
                [matTooltip]="c.materialCount === 0 ? 'An empty catalog cannot be published' : ''"
                data-testid="catalog-publish">
          <mat-icon>publish</mat-icon>
          Publish
        </button>
      </div>
    </div>

    <div class="dl-banner dl-banner--error" *ngIf="actionError">
      <mat-icon>error</mat-icon>
      <div class="dl-banner__body">{{ actionError }}</div>
    </div>

    <div class="dl-card">
      <div class="dl-card__body">
        <div class="dl-kv">
          <div class="dl-kv__item">
            <span class="dl-kv__key">Materials</span>
            <span class="dl-kv__value">{{ c.materialCount }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Packages</span>
            <span class="dl-kv__value">{{ c.packageCount }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Published</span>
            <span class="dl-kv__value">{{ c.publishedAt ? (c.publishedAt | date:'dd.MM.yy HH:mm') : '—' }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Updated</span>
            <span class="dl-kv__value">{{ c.updatedAt ? (c.updatedAt | date:'dd.MM.yy HH:mm') : '—' }}</span>
          </div>
          <div class="dl-kv__item" *ngIf="c.sourceUrl">
            <span class="dl-kv__key">Source</span>
            <span class="dl-kv__value">
              <a [href]="c.sourceUrl" target="_blank" rel="noopener noreferrer">{{ c.sourceUrl }}</a>
            </span>
          </div>
          <div class="dl-kv__item catalog-description" *ngIf="c.description">
            <span class="dl-kv__key">Description</span>
            <span class="dl-kv__value">{{ c.description }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── CSV upload ─────────────────────────────────────────────────────── -->
    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">CSV upload</h2>
        <button type="button" class="dl-btn dl-btn--sm" (click)="downloadTemplate()"
                [disabled]="downloadingTemplate" data-testid="catalog-template">
          <mat-icon>download</mat-icon>
          {{ downloadingTemplate ? 'Downloading…' : 'Download template' }}
        </button>
      </div>
      <div class="dl-card__body">
        <p class="dl-field__hint upload-hint">
          One row per package, header row required, comma or semicolon separated, UTF-8. The file describes the
          whole catalog: materials and packages missing from it will be retired.
        </p>

        <app-file-upload
          acceptedTypes=".csv,text/csv"
          placeholder="Drag the CSV here, or click to choose one"
          [autoUpload]="false"
          [maxSize]="10485760"
          [disabled]="previewing || committing"
          (fileSelected)="onFileSelected($event)"
          (fileRemoved)="onFileRemoved()"
          data-testid="catalog-upload-file">
        </app-file-upload>

        <div class="upload-actions" *ngIf="file && !plan && !previewing && uploadError">
          <button type="button" class="dl-btn" (click)="preview()">Check again</button>
        </div>

        <div class="dl-loading upload-loading" *ngIf="previewing">
          <div class="dl-spinner"></div>
          <p>Reading the file…</p>
        </div>

        <div class="dl-banner dl-banner--error" *ngIf="uploadError">
          <mat-icon>error</mat-icon>
          <div class="dl-banner__body">{{ uploadError }}</div>
        </div>
        <div class="dl-banner dl-banner--success" *ngIf="uploadSuccess" data-testid="catalog-upload-success">
          <mat-icon>check_circle</mat-icon>
          <div class="dl-banner__body">{{ uploadSuccess }}</div>
        </div>
      </div>
    </div>

    <div class="dl-card" *ngIf="plan" data-testid="catalog-upload-plan">
      <div class="dl-card__header">
        <h2 class="dl-card__title">{{ committed ? 'Uploaded' : 'Preview' }}</h2>
      </div>
      <div class="dl-card__body">

        <!-- File-level errors mean no row was read at all, so they come before everything else. -->
        <div class="dl-banner dl-banner--error" *ngIf="plan.fileErrors.length" data-testid="catalog-file-errors">
          <mat-icon>error</mat-icon>
          <div class="dl-banner__body">
            <strong>The file cannot be uploaded:</strong>
            <ul class="file-errors">
              <li *ngFor="let message of plan.fileErrors">{{ message }}</li>
            </ul>
          </div>
        </div>

        <div class="dl-stat-grid upload-totals">
          <div class="dl-stat dl-stat--good"><span class="dl-stat__value">{{ plan.totals.create }}</span><span class="dl-stat__label">new</span></div>
          <div class="dl-stat dl-stat--primary"><span class="dl-stat__value">{{ plan.totals.update }}</span><span class="dl-stat__label">updated</span></div>
          <div class="dl-stat"><span class="dl-stat__value">{{ plan.totals.unchanged }}</span><span class="dl-stat__label">unchanged</span></div>
          <div class="dl-stat"><span class="dl-stat__value">{{ plan.totals.retire }}</span><span class="dl-stat__label">retired</span></div>
          <div class="dl-stat" [class.dl-stat--bad]="plan.totals.error > 0"><span class="dl-stat__value">{{ plan.totals.error }}</span><span class="dl-stat__label">errors</span></div>
        </div>

        <div class="dl-banner dl-banner--error" *ngIf="plan.totals.error > 0">
          <mat-icon>error</mat-icon>
          <div class="dl-banner__body">
            {{ plan.totals.error }} row(s) have errors — the file cannot be uploaded until they are fixed.
          </div>
        </div>

        <div class="upload-actions" *ngIf="!committed">
          <button type="button" class="dl-btn dl-btn--primary"
                  (click)="commit()"
                  [disabled]="blocked || committing"
                  data-testid="catalog-upload-commit">
            <mat-icon>upload</mat-icon>
            {{ committing ? 'Uploading…' : 'Upload' }}
          </button>
          <span class="dl-field__hint">Nothing is written until you press Upload.</span>
        </div>
      </div>

      <table class="dl-table dl-table--cards" dlCardTable *ngIf="plan.rows.length">
        <thead>
          <tr>
            <th>Line</th>
            <th>Key</th>
            <th>Material</th>
            <th>REF</th>
            <th>Package</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of sortedRows; trackBy: trackByIndex"
              [class.row-error]="row.action === 'ERROR'"
              [attr.data-action]="row.action">
            <td class="dl-cell--hide-sm">{{ row.line ?? '—' }}</td>
            <td>{{ row.materialKey || '—' }}</td>
            <td class="dl-cell--title">
              {{ row.materialName || '—' }}
              <div class="row-message" *ngIf="row.message">{{ row.message }}</div>
            </td>
            <td>{{ row.manufacturerRef || '—' }}</td>
            <td>{{ row.packageName || '—' }}</td>
            <td><span [ngClass]="actionClass(row.action)">{{ actionLabel(row.action) }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Materials ──────────────────────────────────────────────────────── -->
    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">Materials</h2>
        <span class="dl-card__subtitle">{{ total }}</span>
      </div>
      <div class="dl-card__body materials-filters">
        <div class="dl-field">
          <label class="dl-field__label" for="materialSearch">Search</label>
          <input id="materialSearch" type="text" class="dl-input" [(ngModel)]="search"
                 (ngModelChange)="onSearch($event)" placeholder="Name, key, family, shade, REF"
                 data-testid="catalog-materials-search" />
        </div>
      </div>

      <div class="dl-banner dl-banner--error materials-error" *ngIf="materialsError">
        <mat-icon>error</mat-icon>
        <div class="dl-banner__body">{{ materialsError }}</div>
      </div>

      <app-empty-state icon="inventory_2" title="No materials"
                       *ngIf="!materialsLoading && !materialsError && materials.length === 0">
        <ng-container *ngIf="search; else emptyCatalog">Nothing matches the search.</ng-container>
        <ng-template #emptyCatalog>The catalog is empty — upload a CSV.</ng-template>
      </app-empty-state>

      <div *ngIf="materials.length > 0" [class.dl-is-refreshing]="materialsLoading">
        <table class="dl-table dl-table--cards" dlCardTable data-testid="catalog-materials-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Name</th>
              <th>Manufacturer</th>
              <th>Unit</th>
              <th>Category</th>
              <th>Family</th>
              <th>Shade</th>
              <th>Packages</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let m of materials; trackBy: trackById"
                [class.is-retired]="!m.active"
                [attr.data-testid]="'catalog-material-' + m.materialKey">
              <td><code>{{ m.materialKey }}</code></td>
              <td class="dl-cell--title">
                {{ m.name }}
                <span class="retired-tag" *ngIf="!m.active">retired</span>
              </td>
              <td>{{ m.manufacturerName || '—' }}</td>
              <td>
                {{ unitLabel(m.unit) }}
                <span class="unit-whole" *ngIf="m.wholeQuantitiesOnly" matTooltip="Whole quantities only">whole</span>
              </td>
              <td>{{ m.categoryName || '—' }}</td>
              <td>{{ m.family || '—' }}</td>
              <td>{{ m.shade || '—' }}</td>
              <td>
                <ul class="package-list" *ngIf="m.packages.length; else noPackages">
                  <li *ngFor="let p of m.packages; trackBy: trackById" [class.is-retired]="!p.active">
                    {{ packageLabel(p) }}
                  </li>
                </ul>
                <ng-template #noPackages>—</ng-template>
              </td>
            </tr>
          </tbody>
        </table>

        <app-pagination
          [currentPage]="page"
          [pageSize]="pageSize"
          [totalElements]="total"
          [totalPages]="totalPages"
          [loading]="materialsLoading"
          itemLabel="materials"
          (pageChange)="goToPage($event)">
        </app-pagination>
      </div>
    </div>
  </ng-container>
</div>

<app-catalog-form-dialog
  [isOpen]="formOpen"
  [catalog]="catalog"
  (saved)="onSaved($event)"
  (cancel)="formOpen = false">
</app-catalog-form-dialog>

<app-confirm-dialog
  [isOpen]="!!pendingAction"
  [title]="confirmTitle"
  [message]="confirmMessage"
  [confirmLabel]="pendingAction === 'publish' ? 'Publish' : 'Archive'"
  cancelLabel="Cancel"
  [confirmClass]="pendingAction === 'publish' ? 'dl-btn--primary' : 'dl-btn--danger'"
  [busy]="actionBusy"
  (confirm)="confirmAction()"
  (cancel)="pendingAction = null">
</app-confirm-dialog>
